/**
 * Device heuristics for the touch interface.
 *
 * A phone or tablet reports a coarse pointer that cannot hover. A touch-screen
 * laptop reports a coarse pointer too, but it also has a fine mouse and can
 * hover, so it keeps the desktop interface until the player actually touches
 * the screen (the game promotes the interface on the first touch event).
 *
 * No heuristic is perfect, so the caller can override the guess with the
 * `?touch=1` / `?touch=0` query switch or the Touch controls setting.
 */
export interface TouchEnvironment {
  matchMedia?: (query: string) => { matches: boolean } | null;
  maxTouchPoints?: number;
}

/** True when the environment looks like a phone or a tablet. */
export function detectTouchDevice(environment: TouchEnvironment = currentEnvironment()): boolean {
  const coarse = matches(environment, '(pointer: coarse)');
  if (!coarse) return false;
  // A coarse pointer with no hover is a phone or tablet. Some browsers report
  // touch points without the media query, so either signal is enough.
  return matches(environment, '(hover: none)') || (environment.maxTouchPoints ?? 0) > 0;
}

function matches(environment: TouchEnvironment, media: string): boolean {
  try {
    return environment.matchMedia?.(media)?.matches === true;
  } catch {
    return false;
  }
}

function currentEnvironment(): TouchEnvironment {
  if (typeof window === 'undefined') return {};
  return {
    matchMedia: window.matchMedia?.bind(window),
    maxTouchPoints: typeof navigator === 'undefined' ? 0 : navigator.maxTouchPoints,
  };
}
